/**
 * THE LOADING RULE'S PURE PARTS (Hamzah, 2026-10-20; the rule in full is in `loader.css`): which
 * screens have had their first open, and when a reload's placeholder may show. Tested without a
 * screen; `ScreenGate.tsx` and `useDelayedPending` are the hooks around them.
 */

/** A reload waits this long before its placeholder shows: a faster one never flashes. */
export const PLACEHOLDER_DELAY_MS = 250;

/**
 * The screens opened in this visit of the console. The full ring is a screen's FIRST open: once a
 * screen has shown its content, opening it again goes straight to it, and anything it then reloads
 * shows its cards' own placeholders. One registry per tab of the browser, gone on a reload of the
 * page, which is a first open again.
 */
export interface OpenedScreens {
  has(key: string): boolean;
  mark(key: string): void;
}

export function openedScreens(): OpenedScreens {
  const seen = new Set<string>();
  return {
    has: (key) => seen.has(key),
    mark: (key) => {
      seen.add(key);
    },
  };
}

/** The console's own registry, shared by every `ScreenGate`. */
export const OPENED = openedScreens();

/**
 * Whether a reload's placeholder shows now: only while still pending, and only once it has been
 * pending for `delayMs`. `since` is when the wait began (null while nothing is pending).
 */
export function placeholderShown(o: { pending: boolean; since: number | null; now: number; delayMs?: number }): boolean {
  if (!o.pending || o.since === null) return false;
  return o.now - o.since >= (o.delayMs ?? PLACEHOLDER_DELAY_MS);
}

/**
 * Whether a first open may show its screen: once the gate's own rule is met (every first fetch in,
 * or its cap), or as soon as the screen has said its essentials are in (`useScreenReady`).
 * `declared` is null for a screen that says nothing.
 */
export function gateReady(o: { firstLoaded: boolean; declared: boolean | null }): boolean {
  return o.firstLoaded || o.declared === true;
}
